"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { CheckIcon } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

interface ColorPickerProps
  extends Omit<React.ComponentProps<"input">, "onChange" | "type"> {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  colors?: string[];
  /** Colors already used in the workspace, shown as a separate section */
  usedColors?: string[];
  className?: string;
  name?: string;
}

const DEFAULT_COLORS = [
  "#3b82f6", // Blue - Good contrast in both modes
  "#10b981", // Emerald - Accessible green
  "#f59e0b", // Amber - High contrast yellow/orange
  "#ef4444", // Red - Classic error/danger color
  "#8b5cf6", // Violet - Distinctive purple
  "#ec4899", // Pink - Softer accent
  "#14b8a6", // Teal - Calming blue-green
  "#f97316", // Orange - Warm accent
  "#6366f1", // Indigo - Deep blue-purple
  "#84cc16", // Lime - Bright green
];

const COLOR_LABELS: Record<string, string> = {
  "#3b82f6": "Blue",
  "#10b981": "Emerald",
  "#f59e0b": "Amber",
  "#ef4444": "Red",
  "#8b5cf6": "Violet",
  "#ec4899": "Pink",
  "#14b8a6": "Teal",
  "#f97316": "Orange",
  "#6366f1": "Indigo",
  "#84cc16": "Lime",
};

const ColorPicker = React.forwardRef<HTMLInputElement, ColorPickerProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      colors = DEFAULT_COLORS,
      usedColors,
      className,
      name,
      disabled,
      ...props
    },
    ref,
  ) => {
    const { t } = useLingui();
    const [internalValue, setInternalValue] = React.useState(
      defaultValue || colors[0],
    );
    const [open, setOpen] = React.useState(false);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;

    // Deduplicate usedColors and exclude colors already in the default palette
    const filteredUsedColors = React.useMemo(() => {
      if (!usedColors || usedColors.length === 0) return [];
      const defaultSet = new Set(colors.map((c) => c.toLowerCase()));
      const seen = new Set<string>();
      return usedColors.filter((c) => {
        const lower = c.toLowerCase();
        if (defaultSet.has(lower) || seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
    }, [usedColors, colors]);

    const handleColorChange = (newColor: string) => {
      if (!isControlled) {
        setInternalValue(newColor);
      }
      onChange?.(newColor);
      setOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Input
          ref={ref}
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          name={name}
          disabled={disabled}
          className="flex-1 font-mono"
          placeholder="#000000"
          aria-label="Color value"
          pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
          title={t`Please enter a valid hex color code (e.g., #123456 or #abc)`}
          {...props}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                "h-9 w-9 rounded-md border transition-all",
                "focus:outline-none focus:ring-2 focus:ring-offset-2",
                "focus:ring-ring disabled:pointer-events-none disabled:opacity-50",
              )}
              style={{ backgroundColor: currentValue }}
              aria-label={`Color preview: ${currentValue && COLOR_LABELS[currentValue] ? COLOR_LABELS[currentValue] : currentValue}. Click to open color picker`}
            >
              <span className="sr-only">Open color picker</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="end">
            <div className="space-y-3">
              {filteredUsedColors.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    <Trans>Used in workspace</Trans>
                  </div>
                  <div
                    className="grid grid-cols-5 gap-2"
                    role="radiogroup"
                    aria-label="Used workspace colors"
                  >
                    {filteredUsedColors.map((color) => {
                      const isSelected = currentValue === color;
                      const label = COLOR_LABELS[color] || color;

                      return (
                        <button
                          key={color}
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          aria-label={`Select ${label} color`}
                          onClick={() => handleColorChange(color)}
                          className={cn(
                            "relative h-8 w-8 rounded-md transition-all",
                            "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2",
                            "focus:ring-ring disabled:pointer-events-none disabled:opacity-50",
                            isSelected && "ring-2 ring-offset-2 ring-ring",
                          )}
                          style={{ backgroundColor: color }}
                          disabled={disabled}
                        >
                          {isSelected && (
                            <CheckIcon
                              className={cn(
                                "absolute inset-0 m-auto h-4 w-4",
                                "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]",
                              )}
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  <Trans>Select a color</Trans>
                </div>
                <div
                  className="grid grid-cols-5 gap-2"
                  role="radiogroup"
                  aria-label="Color picker"
                >
                  {colors.map((color) => {
                    const isSelected = currentValue === color;
                    const label = COLOR_LABELS[color] || color;

                    return (
                      <button
                        key={color}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        aria-label={`Select ${label} color`}
                        onClick={() => handleColorChange(color)}
                        className={cn(
                          "relative h-8 w-8 rounded-md transition-all",
                          "hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2",
                          "focus:ring-ring disabled:pointer-events-none disabled:opacity-50",
                          isSelected && "ring-2 ring-offset-2 ring-ring",
                        )}
                        style={{ backgroundColor: color }}
                        disabled={disabled}
                      >
                        {isSelected && (
                          <CheckIcon
                            className={cn(
                              "absolute inset-0 m-auto h-4 w-4",
                              "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]",
                            )}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

ColorPicker.displayName = "ColorPicker";

export { ColorPicker, DEFAULT_COLORS };
